import type { Profile, User } from "@fusioncraft/shared";
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { AuthScreen } from "./components/AuthScreen";
import { Game } from "./components/Game";
import { Onboarding } from "./components/Onboarding";

const LAST_PROFILE = "fc:lastProfile";

function rememberProfile(id: string) {
  try {
    localStorage.setItem(LAST_PROFILE, id);
  } catch {}
}

function recallProfile(): string | null {
  try {
    return localStorage.getItem(LAST_PROFILE);
  } catch {
    return null;
  }
}

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    api
      .me()
      .then((r) => setUser(r.user))
      .catch((e) => setBootError(e.message));
  }, []);

  const loadProfiles = useCallback(async () => {
    const { profiles } = await api.profiles();
    setProfiles(profiles);
    setActiveId((current) => {
      const wanted = current ?? recallProfile();
      return profiles.find((p) => p.id === wanted)?.id ?? profiles[0]?.id ?? null;
    });
    return profiles;
  }, []);

  useEffect(() => {
    if (user) loadProfiles().catch((e) => setBootError(e.message));
    else {
      setProfiles(null);
      setActiveId(null);
    }
  }, [user, loadProfiles]);

  useEffect(() => {
    if (activeId) rememberProfile(activeId);
  }, [activeId]);

  if (bootError) {
    return (
      <div className="splash">
        <p>{bootError}</p>
        <button className="btn" onClick={() => location.reload()}>
          Retry
        </button>
      </div>
    );
  }
  if (user === undefined || (user && profiles === null)) return <div className="splash" aria-busy="true" />;
  if (!user) return <AuthScreen onAuthed={setUser} />;

  if (!profiles!.length || !activeId) {
    return (
      <Onboarding
        user={user}
        onCreated={(p) => {
          setProfiles([p]);
          setActiveId(p.id);
        }}
        onSignOut={() => api.logout().finally(() => setUser(null))}
      />
    );
  }

  return (
    <Game
      key={activeId}
      user={user}
      profiles={profiles!}
      activeId={activeId}
      onSwitch={setActiveId}
      onProfilesChanged={loadProfiles}
      onUserChanged={setUser}
      onSignOut={() => api.logout().finally(() => setUser(null))}
    />
  );
}
